import { useTranslation } from 'module/hooks/use-translation';
import {
  EmptyState,
  EmptyStateContainer as EmptyStateContainerAtom,
  EmptyStateIcon,
} from 'module/ui/components/atoms/empty-state';
import { Restart } from '@carbon/icons-react';
import { faExclamationTriangle } from '@fortawesome/pro-duotone-svg-icons';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const EmptyStateContainer = styled(EmptyStateContainerAtom)`
  && {
    padding: var(--spacing-12);
  }
  width: auto;
  max-width: max-content;
`;

const ErrorInfo = styled.div`
  margin-bottom: var(--spacing-06);
`;

const LoginLink = styled.a`
  gap: var(--spacing-03);
`;

const domainName = window.location.hostname;

interface Props {
  error: Error;
  supportEmail: string;
  loginUrl: string;
}

const OktaTimeJWTErrors = [
  'The JWT was issued in the future',
  'The JWT expired and is no longer valid',
];

const EmailNotPresent = [
  'User creation was disabled.',
  'User is not assigned to the client application.',
  'api key creation failed',
];

export const LoginCallbackErrorPage: FC<Props> = ({
  error,
  loginUrl,
  supportEmail,
}) => {
  const mailSubject = useTranslation('okta.callback_error.email.subject', {
    domainName,
  });
  const mailContent = useTranslation('okta.callback_error.email.content', {
    domainName,
  });

  const ErrorMessage = OktaTimeJWTErrors.includes(error.message) ? (
    <FormattedMessage id="okta.callback_error.info_timezone" />
  ) : (
    <FormattedMessage id="okta.callback_error.info" />
  );

  const ErrorTitle = EmailNotPresent.includes(error.message) ? (
    <FormattedMessage id="okta.callback_error_email.not.found" />
  ) : (
    <FormattedMessage id="okta.callback_error.title" />
  );

  return (
    <EmptyStateContainer $transparent>
      <EmptyState
        image={<EmptyStateIcon icon={faExclamationTriangle} />}
        title={ErrorTitle}
        message={
          !EmailNotPresent.includes(error.message) && (
            <>
              <ErrorInfo>
                <FormattedMessage id="failure" /> : {error.message}
              </ErrorInfo>
              {ErrorMessage}
            </>
          )
        }
        primaryCTA={
          <EmptyState.CallToAction.Primary asChild>
            <LoginLink href={loginUrl}>
              <Restart size={16} />
              {!EmailNotPresent.includes(error.message) ? (
                <FormattedMessage id="okta.callback_error.cta" />
              ) : (
                <FormattedMessage id="okta.callback_error.cta.different_id.error" />
              )}
            </LoginLink>
          </EmptyState.CallToAction.Primary>
        }
        secondaryCTA={
          <FormattedMessage
            id="okta.callback_error.info_extra"
            values={{
              link: (
                <a
                  target="_blank"
                  href={`mailto:${supportEmail}?subject=${mailSubject}&body=${mailContent}`}
                  rel="noreferrer"
                >
                  {supportEmail}
                </a>
              ),
            }}
          />
        }
      />
    </EmptyStateContainer>
  );
};
